/**
 * Public-surface URLs for the loyalty pages — ONE source of truth for the host.
 *
 * The guest page (/v/:code), the Kitchen display (/kitchen/:code) and the
 * Cashier till (/cashier/:code) are all routes of THIS SPA. So the host that
 * reaches them is always the host the owner is already looking at — which is
 * exactly what `window.location.origin` is.
 *
 * ⚠️ Why this file exists. Every human-readable label used to be the literal
 * string `popuppush.com/...`, chosen as a friendly signpost on the assumption
 * that the apex would one day serve this SPA. It does not: `popuppush.com` and
 * `www.popuppush.com` are the MARKETING site (Firebase site `popuppush-landing`,
 * whose `**` → /index.html rewrite serves the homepage at every path). So every
 * printed label pointed staff at a page that renders marketing copy and no
 * kitchen board — on prod AND on dev, since the literal was environment-blind.
 *
 * The QR *encoders* were always right (`${window.location.origin}/v/...`), which
 * is why scanning worked while reading the label did not. This centralises that
 * already-correct pattern so the label and the QR can never disagree again.
 *
 * Deliberately NOT driven by an env var: `REACT_APP_BASE_URL` exists in both
 * .env files, is referenced nowhere, and its prod value is `www.popuppush.com`
 * — the marketing host. Wiring it in would have re-created the same bug from a
 * different direction. The live origin cannot drift from where the app is
 * actually served.
 */

/** Last-resort host for non-browser contexts (SSR//tests that don't stub a DOM).
 *  Never reached in the app itself; kept so callers never interpolate
 *  "undefined" into a URL a human might act on. */
const FALLBACK_ORIGIN = 'https://app.popuppush.com';

/** The scheme+host this SPA is being served from, e.g. `https://app.popuppush.com`. */
export function publicOrigin() {
  if (typeof window === 'undefined' || !window.location?.origin) return FALLBACK_ORIGIN;
  return window.location.origin;
}

/** Bare host for display, e.g. `app.popuppush.com`. No scheme, no trailing slash. */
export function prettyHost() {
  return publicOrigin().replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

/**
 * A full, clickable URL for a public path.
 * `publicUrl('/kitchen/my-cafe')` → `https://app.popuppush.com/kitchen/my-cafe`
 */
export function publicUrl(path = '') {
  const p = String(path);
  return `${publicOrigin()}${p.startsWith('/') ? p : `/${p}`}`;
}

/**
 * The same URL without the scheme — what a human reads on screen or on a poster.
 * `prettyUrl('/v/my-cafe')` → `app.popuppush.com/v/my-cafe`
 */
export function prettyUrl(path = '') {
  const p = String(path);
  return `${prettyHost()}${p.startsWith('/') ? p : `/${p}`}`;
}
